import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Leaf,
  Settings,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Globe,
  ChevronRight,
  FileText,
  Users,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ========== SEASONAL PRODUCTS TAB COMPONENT ==========
function SeasonalProductsTab({ country }) {
  const [seasonalConfig, setSeasonalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const fetchSeasonalConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/food-agriculture/admin/seasonal-config/${country}`
      );
      const data = await response.json();
      if (data.success) {
        setSeasonalConfig(data.config);
      }
    } catch (error) {
      console.error('Failed to fetch seasonal config:', error);
      toast.error('Failed to fetch seasonal configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeasonalConfig();
  }, [country]);

  const toggleSeasonalSection = async (enabled) => {
    try {
      const response = await fetch(
        `${API_URL}/api/food-agriculture/admin/seasonal-config/${country}/toggle?enabled=${enabled}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchSeasonalConfig();
      }
    } catch (error) {
      toast.error('Failed to toggle seasonal section');
    }
  };

  const saveProduct = async (product) => {
    try {
      const response = await fetch(
        `${API_URL}/api/food-agriculture/admin/seasonal-product/${country}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(product),
        }
      );
      const data = await response.json();
      if (data.success) {
        toast.success('Seasonal product saved');
        fetchSeasonalConfig();
        setEditingProduct(null);
        setShowAddDialog(false);
      }
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this seasonal product?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/food-agriculture/admin/seasonal-product/${country}/${productId}`,
        { method: 'DELETE' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success('Product deleted');
        fetchSeasonalConfig();
      }
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];

  const subcategories = [
    { slug: 'fresh-produce', name: 'Fresh Produce' },
    { slug: 'livestock-poultry', name: 'Livestock & Poultry' },
    { slug: 'seeds-plants', name: 'Seeds & Plants' },
    { slug: 'fertilisers', name: 'Fertilisers' },
    { slug: 'farm-machinery', name: 'Farm Machinery' },
    { slug: 'animal-feed', name: 'Animal Feed' },
    { slug: 'organic-products', name: 'Organic Products' },
    { slug: 'fisheries', name: 'Fisheries' },
  ];

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-48 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-100 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-green-600" />
              What's In Season
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Manage seasonal product highlights for {country === 'UK' ? 'United Kingdom' : 'Sri Lanka'}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="seasonal-toggle" className="text-sm">Section Enabled</Label>
              <Switch 
                id="seasonal-toggle"
                checked={seasonalConfig?.enabled ?? true}
                onCheckedChange={toggleSeasonalSection}
                data-testid="seasonal-section-toggle"
              />
            </div>
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-seasonal-product">
              + Add Product
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Section Settings */}
        <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="text-xs text-gray-500">Section Title</Label>
            <p className="font-medium">{seasonalConfig?.section_title || 'What\'s In Season'}</p>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Section Subtitle</Label>
            <p className="text-sm text-gray-600">{seasonalConfig?.section_subtitle || '-'}</p>
          </div>
        </div>

        {/* Products Table */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Subcategory</TableHead>
              <TableHead>Season</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {seasonalConfig?.products?.map((product) => (
              <TableRow key={product.id} data-testid={`seasonal-row-${product.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{product.icon}</span>
                    <div>
                      <p className="font-medium">{product.title}</p>
                      <p className="text-xs text-gray-500 line-clamp-1">{product.description}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{product.subcategory_slug}</Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{product.season_start} - {product.season_end}</span>
                </TableCell>
                <TableCell>
                  {product.is_active ? (
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500">{product.display_order}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setEditingProduct(product)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => deleteProduct(product.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {(!seasonalConfig?.products || seasonalConfig.products.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No seasonal products configured. Click "Add Product" to create one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Add/Edit Product Dialog */}
        {(showAddDialog || editingProduct) && (
          <SeasonalProductDialog
            product={editingProduct}
            onSave={saveProduct}
            onClose={() => {
              setShowAddDialog(false);
              setEditingProduct(null);
            }}
            months={months}
            subcategories={subcategories}
          />
        )}
      </CardContent>
    </Card>
  );
}

// Product Edit Dialog Component
function SeasonalProductDialog({ product, onSave, onClose, months, subcategories }) {
  const [formData, setFormData] = useState(product || {
    title: '',
    description: '',
    subcategory_slug: 'fresh-produce',
    icon: '🌱',
    image_url: '',
    season_start: 'January',
    season_end: 'December',
    highlight_text: '',
    filter_tags: [],
    is_active: true,
    display_order: 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">
          {product ? 'Edit Seasonal Product' : 'Add Seasonal Product'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Title *</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., British Asparagus Season"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Description *</Label>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this seasonal highlight"
              />
            </div>

            <div>
              <Label>Icon</Label>
              <Input 
                value={formData.icon}
                onChange={(e) => setFormData({...formData, icon: e.target.value})}
                placeholder="🌱"
              />
            </div>

            <div>
              <Label>Subcategory</Label>
              <Select 
                value={formData.subcategory_slug}
                onValueChange={(v) => setFormData({...formData, subcategory_slug: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {subcategories.map(s => (
                    <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Season Start</Label>
              <Select 
                value={formData.season_start}
                onValueChange={(v) => setFormData({...formData, season_start: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Season End</Label>
              <Select 
                value={formData.season_end}
                onValueChange={(v) => setFormData({...formData, season_end: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Highlight Text</Label>
              <Input 
                value={formData.highlight_text || ''}
                onChange={(e) => setFormData({...formData, highlight_text: e.target.value})}
                placeholder="e.g., In Season Now"
              />
            </div>

            <div>
              <Label>Display Order</Label>
              <Input 
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
              />
            </div>

            <div className="col-span-2">
              <Label>Image URL</Label>
              <Input 
                value={formData.image_url || ''}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://images.unsplash.com/..."
              />
            </div>

            <div className="col-span-2">
              <Label>Filter Tags (comma-separated)</Label>
              <Input 
                value={(formData.filter_tags || []).join(', ')}
                onChange={(e) => setFormData({
                  ...formData, 
                  filter_tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                })}
                placeholder="asparagus, spring vegetables"
              />
            </div>

            <div className="col-span-2 flex items-center gap-2">
              <Switch 
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({...formData, is_active: v})}
              />
              <Label>Active</Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">Save Product</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ========== MAIN COMPONENT ==========
export default function AdminFoodAgriculture() {
  const [selectedCountry, setSelectedCountry] = useState('UK');
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('subcategories');

  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, [selectedCountry]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/food-agriculture/config/subcategories?country=${selectedCountry}`
      );
      const data = await response.json();
      if (data.success) {
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
      toast.error('Failed to fetch configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/food-agriculture/admin/stats?country=${selectedCountry}`
      );
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const toggleAgroService = async (subcategorySlug, serviceKey, enabled) => {
    try {
      const response = await fetch(
        `${API_URL}/api/food-agriculture/admin/agro-services/toggle?country=${selectedCountry}&subcategory_slug=${subcategorySlug}&service_key=${serviceKey}&enabled=${enabled}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchConfig();
      } else {
        toast.error('Failed to toggle service');
      }
    } catch (error) {
      console.error('Failed to toggle service:', error);
      toast.error('Failed to toggle service');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-10 w-64 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-food-agriculture">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white">
              <Leaf className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Food & Agriculture Admin</h1>
              <p className="text-gray-500">Manage services and configurations</p>
            </div>
          </div>
          
          {/* Country Selector */}
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-gray-400" />
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-40" data-testid="country-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { fetchConfig(); fetchStats(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.providers?.total || 0}</p>
                    <p className="text-sm text-gray-500">Total Providers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.providers?.active || 0}</p>
                    <p className="text-sm text-gray-500">Active Providers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.inquiries || 0}</p>
                    <p className="text-sm text-gray-500">Inquiries</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.rfqs || 0}</p>
                    <p className="text-sm text-gray-500">RFQs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Country Info Banner */}
        <Card className="bg-gradient-to-r from-green-50 to-teal-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{selectedCountry === 'UK' ? '🇬🇧' : '🇱🇰'}</span>
                <div>
                  <h2 className="font-semibold text-green-800">{config?.country}</h2>
                  <p className="text-sm text-green-600">
                    {config?.total_subcategories} subcategories · {config?.currency} ({config?.currency_symbol})
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={config?.agro_services_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                  <Leaf className="w-3 h-3 mr-1" />
                  Agro Services {config?.agro_services_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
            {config?.agro_services_disclaimer && (
              <div className="mt-2 flex items-center gap-2 text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4" />
                <span>{config.agro_services_disclaimer}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="subcategories" data-testid="tab-subcategories">
              <Settings className="w-4 h-4 mr-2" />
              Subcategories
            </TabsTrigger>
            <TabsTrigger value="seasonal" data-testid="tab-seasonal">
              <Leaf className="w-4 h-4 mr-2" />
              Seasonal
            </TabsTrigger>
            <TabsTrigger value="agro-services" data-testid="tab-agro">
              <Leaf className="w-4 h-4 mr-2" />
              Agro Services
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Seasonal Products Tab */}
          <TabsContent value="seasonal">
            <SeasonalProductsTab country={selectedCountry} />
          </TabsContent>

          {/* Subcategories Tab */}
          <TabsContent value="subcategories">
            <Card>
              <CardHeader>
                <CardTitle>Subcategory Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Subcategory</TableHead>
                      <TableHead>Services</TableHead>
                      <TableHead>Compliance</TableHead>
                      <TableHead>Card Type</TableHead>
                      <TableHead>Agro Services</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {config?.subcategories?.map((subcat) => (
                      <TableRow key={subcat.slug} data-testid={`subcat-row-${subcat.slug}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{subcat.subcategory_icon}</span>
                            <div>
                              <p className="font-medium">{subcat.subcategory_name}</p>
                              <p className="text-xs text-gray-500">{subcat.slug}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {Object.keys(subcat.services || {}).length} services
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={
                              subcat.compliance_level === 'high' ? 'bg-red-100 text-red-700' :
                              subcat.compliance_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }
                          >
                            {subcat.compliance_level}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{subcat.card_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {subcat.has_agro_services ? (
                            <Badge className="bg-teal-100 text-teal-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {subcat.agro_services?.length || 0} linked
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">
                              <XCircle className="w-3 h-3 mr-1" />
                              None
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agro Services Tab */}
          <TabsContent value="agro-services">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-teal-600" />
                  Agro Advisory Services
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {config?.subcategories?.filter(s => s.has_agro_services).map((subcat) => (
                    <Card key={subcat.slug} className="border-l-4 border-l-teal-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{subcat.subcategory_icon}</span>
                            <h3 className="font-semibold">{subcat.subcategory_name}</h3>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(subcat.services || {})
                            .filter(([_, service]) => service.is_agro_service)
                            .map(([key, service]) => (
                              <div key={key} className="flex items-center justify-between p-3 bg-teal-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <span>{service.icon}</span>
                                  <div>
                                    <p className="font-medium text-sm">{service.name}</p>
                                    <p className="text-xs text-gray-500">{service.description}</p>
                                  </div>
                                </div>
                                <Switch
                                  defaultChecked={true}
                                  onCheckedChange={(checked) => toggleAgroService(subcat.slug, key, checked)}
                                />
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Enable Agro Services</Label>
                      <p className="text-sm text-gray-500">Allow agro advisory services</p>
                    </div>
                    <Switch defaultChecked={config?.agro_services_enabled} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Require Provider Verification</Label>
                      <p className="text-sm text-gray-500">All providers must be verified</p>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Enable RFQ System</Label>
                      <p className="text-sm text-gray-500">Allow bulk sourcing requests</p>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Disclaimers</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Agro Services Disclaimer</Label>
                    <Input 
                      className="mt-2"
                      defaultValue={config?.agro_services_disclaimer}
                      placeholder="Enter disclaimer text..."
                    />
                  </div>
                  <div>
                    <Label>Livestock Disclaimer</Label>
                    <Input 
                      className="mt-2"
                      defaultValue="No veterinary services provided. No certification claims."
                      placeholder="Enter disclaimer text..."
                    />
                  </div>
                  <Button className="w-full bg-green-600 hover:bg-green-700">
                    Save Settings
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
